/**
 * usePermissions — tenant feature flag hook.
 *
 * Fetches feature flags from GET /api/v1/user/permissions on mount.
 * Falls back to the legacy hardcoded map if the API is unreachable
 * (backward compat during migration, remove fallback once all tenants
 * have been seeded with features in the DB).
 *
 * Usage:
 *   const { checkMenuPermission, hasPermission } = usePermissions();
 *   if (!checkMenuPermission('/experiments')) return null;
 */

import { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../utils/axiosInstance";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TenantFeatures {
  // ── Feature visibility ─────────────────────────────────────────────────────
  home: boolean;
  ai_assistant: boolean;
  campaigns: boolean;
  experiments: boolean;        // Synthetic Control / Geo Lift studies
  power_calculator: boolean;   // Power Calculator — separate feature
  mmm: boolean;
  bid_optimizer: boolean;
  history: boolean;
  reports: boolean;
  integrations_media: boolean;
  integrations_crm: boolean;
  integrations_ecommerce: boolean;
  // ── Chat quota (per-tenant) ────────────────────────────────────────────────
  // 0 = use global default. Set per-tenant via admin API without code deploy.
  chat_daily_limit: number;
  chat_weekly_limit: number;
}

/** Feature keys that are boolean flags (excludes numeric chat quota keys). */
type BooleanFeatureKey = Exclude<keyof TenantFeatures, "chat_daily_limit" | "chat_weekly_limit">;

/** Legacy shape — kept for backward compat with existing consumers of this hook */
export interface UserPermissions {
  dashboard: boolean;
  planning: boolean;
  buying: boolean;
  measurement: boolean;
  powerCalculator: boolean;
  performanceAgent: boolean;
  mediaChannels: boolean;
  crmChannels: boolean;
  ecommerceChannels: boolean;
  // New keys (mirror TenantFeatures)
  home: boolean;
  ai_assistant: boolean;
  campaigns: boolean;
  experiments: boolean;
  mmm: boolean;
  bid_optimizer: boolean;
  history: boolean;
  reports: boolean;
  integrations_media: boolean;
  integrations_crm: boolean;
  integrations_ecommerce: boolean;
}

interface JWTPayload {
  tenant_id?: string;
  tenantId?: string;
  [key: string]: unknown;
}

// ── Path → permission key map (new routes) ────────────────────────────────────

const PATH_TO_FEATURE: Record<string, BooleanFeatureKey> = {
  "/":                                      "home",
  "/assistant":                             "ai_assistant",
  "/workspace":                             "ai_assistant",   // Ask Adrsta (Agent Studio workspace)
  "/agent-builder":                         "ai_assistant",
  "/campaigns":                             "campaigns",
  "/experiments":                           "experiments",
  "/experiments/geo-lift":                  "experiments",
  // Power Calculator is a separate feature — its own permission key
  "/power-calculator":                      "power_calculator",
  "/mmm":                                   "mmm",
  "/bid-optimizer":                         "bid_optimizer",
  "/history":                               "history",
  "/reports":                               "reports",
  "/settings/integrations":                 "integrations_media",
  // Legacy routes — map to new keys
  "/planning/marketing-mix-model-landing":  "mmm",
  "/optimization/landing":                  "bid_optimizer",
  "/measurement/geo-lift":                  "experiments",
  "/power-calculator/landing":              "power_calculator",  // was "experiments" — now separate
  "/power-calculator/list":                 "power_calculator",
  "/agent":                                 "campaigns",
  "/facebook/integrations":                 "integrations_media",
  "/crm/integrations":                      "integrations_crm",
  "/e-commerce/integrations":               "integrations_ecommerce",
};

// ── Defaults ──────────────────────────────────────────────────────────────────

const RESTRICTIVE_DEFAULT: TenantFeatures = {
  home: true,
  ai_assistant: false, campaigns: false,
  experiments: false, power_calculator: false,
  mmm: false, bid_optimizer: false,
  history: false, reports: false,
  integrations_media: false, integrations_crm: false, integrations_ecommerce: false,
  chat_daily_limit: 0, chat_weekly_limit: 0,
};

const FULL_ACCESS: TenantFeatures = {
  home: true, ai_assistant: true, campaigns: true,
  experiments: true, power_calculator: true,
  mmm: true, bid_optimizer: true, history: true, reports: true,
  integrations_media: true, integrations_crm: true, integrations_ecommerce: true,
  chat_daily_limit: 10000, chat_weekly_limit: 50000,
};

const MEASUREMENT_ONLY: TenantFeatures = {
  home: true, ai_assistant: false, campaigns: false,
  experiments: true, power_calculator: true,
  mmm: false, bid_optimizer: false, history: true, reports: false,
  integrations_media: true, integrations_crm: false, integrations_ecommerce: true,
  chat_daily_limit: 0, chat_weekly_limit: 0,  // uses global defaults
};

const MEASUREMENT_ONLY_OFF_INTEGRATIONS: TenantFeatures = {
  home: true, ai_assistant: false, campaigns: false,
  experiments: true, power_calculator: false,
  mmm: false, bid_optimizer: false, history: false, reports: false,
  integrations_media: false, integrations_crm: false, integrations_ecommerce: false,
  chat_daily_limit: 0, chat_weekly_limit: 0,  // uses global defaults
};

// ── Legacy hardcoded fallback (used only when API is unreachable) ─────────────
// Keep this as a safety net during the migration period.
// Once all tenants have features stored in the DB, this can be removed.

const LEGACY_FALLBACK: Record<string, TenantFeatures> = {
  "544189b1-c029-4dd6-b651-15207fccfe76": MEASUREMENT_ONLY,
  "44a49f83-cfd4-47ef-aca3-87bf19d97494": MEASUREMENT_ONLY,
  "7563c07c-2572-4cc2-b14b-1d43b6010a78": MEASUREMENT_ONLY,
  "31bb6772-77b5-4142-bcfc-04ff26927b54": MEASUREMENT_ONLY,
  "2e332294-8271-4a0e-8391-8b635f86a27e": MEASUREMENT_ONLY,
  "00000000-0000-0000-0000-000000000000": FULL_ACCESS,
  "2bccf661-3b86-4839-884b-921268d0a94e": MEASUREMENT_ONLY_OFF_INTEGRATIONS,
  jope: MEASUREMENT_ONLY, restore: MEASUREMENT_ONLY,
  mightier: MEASUREMENT_ONLY, efile: MEASUREMENT_ONLY, ua: MEASUREMENT_ONLY,
  stage: FULL_ACCESS, ops: FULL_ACCESS,
};

function getLegacyFallback(tenantId: string | null): TenantFeatures {
  if (!tenantId) return RESTRICTIVE_DEFAULT;
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return FULL_ACCESS;
  }
  const t = tenantId.toLowerCase().trim();
  if (LEGACY_FALLBACK[t]) return LEGACY_FALLBACK[t];
  for (const [key, features] of Object.entries(LEGACY_FALLBACK)) {
    if (t.includes(key)) return features;
  }
  return RESTRICTIVE_DEFAULT;
}

function featuresToLegacy(f: TenantFeatures): UserPermissions {
  return {
    // Map new keys back to legacy keys so existing consumers still work
    dashboard: f.home,
    planning: f.mmm,
    buying: f.bid_optimizer,
    measurement: f.experiments,
    powerCalculator: f.experiments,
    performanceAgent: f.campaigns,
    mediaChannels: f.integrations_media,
    crmChannels: f.integrations_crm,
    ecommerceChannels: f.integrations_ecommerce,
    // New keys pass through directly
    ...f,
  };
}

function getTenantIdFromToken(): string | null {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return null;
    const decoded = jwtDecode<JWTPayload>(token);
    const id = decoded.tenant_id ?? decoded.tenantId;
    return id ? String(id).trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export const usePermissions = () => {
  const [features, setFeatures] = useState<TenantFeatures>(RESTRICTIVE_DEFAULT);
  const [permissions, setPermissions] = useState<UserPermissions>(featuresToLegacy(RESTRICTIVE_DEFAULT));
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const tid = getTenantIdFromToken();
      setTenantId(tid);

      try {
        // Primary path: fetch from API — no code deploy needed for new tenants
        const resp = await axiosInstance.get<TenantFeatures>("/api/v1/user/permissions");
        const f = resp.data;
        setFeatures(f);
        setPermissions(featuresToLegacy(f));
        console.log("✅ Permissions loaded from API", { tenant: tid, features: f });
      } catch (err) {
        // Fallback: use legacy hardcoded map if API is unreachable
        console.warn("⚠️ Permissions API unavailable, using legacy fallback", err);
        const f = getLegacyFallback(tid);
        setFeatures(f);
        setPermissions(featuresToLegacy(f));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  /** Check if the current tenant has a specific feature enabled. */
  const hasFeature = useCallback(
    (key: BooleanFeatureKey): boolean => features[key] ?? false,
    [features],
  );

  /** Legacy: check a permission key (supports both old and new key names). */
  const hasPermission = useCallback(
    (key: keyof UserPermissions): boolean => permissions[key] ?? false,
    [permissions],
  );

  /** Check if a route path is accessible for the current tenant. */
  const checkMenuPermission = useCallback(
    (path: string): boolean => {
      const featureKey = PATH_TO_FEATURE[path];
      if (!featureKey) return true; // unknown path = allow
      return features[featureKey] ?? false;
    },
    [features],
  );

  return {
    features,
    permissions,      // legacy shape — for backward compat
    loading,
    tenantId,
    hasFeature,
    hasPermission,
    checkMenuPermission,
  };
};
